/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type ReactNode } from 'react';
import { css, jsx } from '@compiled/react';
import { FabricElementsAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import { messages } from './i18n';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlaskit/platform-feature-flags';

import { useIntl } from 'react-intl-next';

const oldListStyles = css({
	listStyleType: 'none',
	paddingLeft: 0,
});

const listStyles = css({
	listStyleType: 'none',
	paddingLeft: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div + div': {
		marginTop: token('space.050'),
	},
});

const taskListStyles = css({
	marginTop: token('space.050', '4px'),
});

export interface Props {
	children?: ReactNode;
	listId?: string;
}

const TaskList = ({ listId, children }: Props) => {
	const listSize = React.Children.count(children);

	const { formatMessage } = useIntl();

	if (!children) {
		return null;
	}

	// Data attributes are required for copy and paste from rendered content
	// to the editor to preserve the task.
	// This allows the editor to differentiate between numbered and ordered lists,
	// and action items, which all share the common '‹li›' element.
	// The value of 'data-task-local-id' should be discarded upon paste, with a
	// a new uuid generated by the editor for the cloned content.
	return (
		<div
			role="group"
			css={[fg('platform_editor_fix_missing_task_id') ? listStyles : oldListStyles]}
			data-task-list-local-id=""
			aria-label={formatMessage(messages.fieldsetLabel)}
		>
			{React.Children.map(children, (child, idx) => {
				const { localId } = (child as React.ReactElement).props as {
					localId: string;
				};
				return (
					<FabricElementsAnalyticsContext
						data={{
							listLocalId: listId,
							listSize,
							position: idx,
						}}
					>
						{
							/* The data-task-local-id attribute will be moved to the Editor renderer node
							as the localId isn't guaranteed to be in the direct React child's props) */
							fg('platform_editor_fix_missing_task_id') ? (
								child
							) : (
								<div key={idx} data-task-local-id={localId || ''} css={taskListStyles}>
									{child}
								</div>
							)
						}
					</FabricElementsAnalyticsContext>
				);
			})}
		</div>
	);
};

export default TaskList;
